import { useState, type MouseEvent, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import {
  AssetLifecycleEventSchema,
  AssetSchema,
  CustodyAssignmentSchema,
  SupplierSchema,
  type AssetDto,
  type CustodyAssignmentDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { ApiError, getData } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { MigrationTrace } from '../migration/MigrationTrace';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ProjectName } from '../crm/common';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { ProcurementSourceText } from './procurement-source';
import { AssetDialog } from './AssetsPage';
import { AcknowledgeDialog, AssignDialog, ReturnDialog, StatusDialog } from './custody';
import {
  ASSETS,
  classLabel,
  currentCustody,
  Custodian,
  R1_STATUSES,
  statusLabel,
  useAssetClasses,
  useAssetTypes,
  useMasters,
} from './shared';

type Action = 'edit' | 'status' | 'assign' | 'return' | 'acknowledge';

/**
 * One asset (`/assets/:id`; ERD §7.4): its register facts, placement, current custodian, the full
 * custody history (never overwritten, FRD-AST-026), the lifecycle events, its documents
 * (FRD-AST-013) and its change history, with the actions the session may take. Related records link to their own pages only where the session may read them.
 */
export function AssetDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const detail = useQuery({
    queryKey: ['assets', 'asset', id],
    queryFn: () => getData(`${ASSETS}/${id}`, AssetSchema),
  });
  const back = (
    <Button component={RouterLink} to="/assets" startIcon={<ArrowBackIcon />}>
      Asset register
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the asset" />;
  if (detail.isError) {
    return <RecordFailure title="Asset" noun="asset" error={detail.error} back={back} />;
  }
  return <AssetRecord asset={detail.data} back={back} created={params.get('created') === '1'} />;
}

function AssetRecord({
  asset,
  back,
  created,
}: {
  asset: AssetDto;
  back: ReactNode;
  created: boolean;
}) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const classes = useAssetClasses();
  const types = useAssetTypes();
  const masters = useMasters();
  const [action, setAction] = useState<Action | null>(null);
  const mine = useMyAssets();
  const isMine = mine.data?.some((row) => row.id === asset.id) ?? false;

  const canChange = can('assets.asset:change');
  const canStatus = canChange && (R1_STATUSES as readonly string[]).includes(asset.lifecycleStatus);
  const canAssign = can('assets.custody_assignment:create') && asset.lifecycleStatus === 'active';
  const canReturn = can('assets.custody_assignment:change') && asset.currentAssignmentId !== null;
  const canAcknowledge =
    can('assets.custody_assignment:change') &&
    isMine &&
    asset.acknowledgementRequired === true &&
    asset.acknowledgedAt === null;
  const open = (next: Action) => (event: MouseEvent<HTMLButtonElement>) => {
    // The dialog returns focus here when it closes.
    event.currentTarget.focus();
    setAction(next);
  };
  const close = () => {
    setAction(null);
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${asset.code} — ${asset.name}`}
        actions={
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {back}
            {canAcknowledge && (
              <Button variant="contained" onClick={open('acknowledge')}>
                Acknowledge
              </Button>
            )}
            {canAssign && (
              <Button variant="outlined" onClick={open('assign')}>
                Assign
              </Button>
            )}
            {canReturn && (
              <Button variant="outlined" onClick={open('return')}>
                Record return
              </Button>
            )}
            {canStatus && (
              <Button variant="outlined" onClick={open('status')}>
                Change status
              </Button>
            )}
            {canChange && (
              <Button variant="contained" onClick={open('edit')}>
                Edit
              </Button>
            )}
          </Stack>
        }
      />
      {created && (
        <Alert severity="success" variant="outlined">
          Asset {asset.code} created. The Asset ID was generated by the system.
        </Alert>
      )}
      <Section title="Asset">
        <Facts
          label="Asset details"
          facts={[
            ['Status', <StatusChip key="s" status={asset.lifecycleStatus} />],
            ['Asset ID', asset.code],
            ['Name', asset.name],
            [
              'Type',
              types.data?.find((t) => t.valueCode === asset.assetTypeCode)?.label ??
                asset.assetTypeCode,
            ],
            ['Class', classLabel(classes.data?.items ?? [], asset.assetClassId)],
            ['Description', asset.description ?? '—'],
            ['Serial number', asset.serialNumber ?? '—'],
            ['Manufacturer', asset.manufacturer ?? '—'],
            ['Model', asset.model ?? '—'],
          ]}
        />
      </Section>
      <Section title="Acquisition and warranty">
        <Facts
          label="Acquisition and warranty"
          facts={[
            ['Purchase date', asset.purchaseDate ?? '—'],
            ['Purchase cost', asset.purchaseCost === null ? '—' : `AED ${asset.purchaseCost}`],
            // Carried only by a migration load (FRD-AST-015); not an accounting value.
            ...(asset.openingAccumulatedDepreciation === null
              ? []
              : ([
                  [
                    'Accumulated depreciation (as migrated)',
                    `AED ${asset.openingAccumulatedDepreciation}`,
                  ],
                ] as [string, string][])),
            ['Supplier', <SupplierName key="sup" id={asset.supplierId} />],
            [
              'Procurement source',
              <ProcurementSourceText
                key="src"
                purchaseOrderId={asset.purchaseOrderId}
                purchaseOrderLineId={asset.purchaseOrderLineId}
                goodsReceiptId={asset.goodsReceiptId}
                goodsReceiptLineId={asset.goodsReceiptLineId}
              />,
            ],
            ['Warranty provider', asset.warrantyProvider ?? '—'],
            ['Warranty expiry', asset.warrantyExpiryDate ?? '—'],
            ['Disposal date', asset.disposalDate ?? '—'],
          ]}
        />
      </Section>
      <Section title="Placement">
        <Facts
          label="Placement"
          facts={[
            ['Location', masters.location(asset.locationId)],
            ['Department', masters.department(asset.departmentId)],
            ['Cost centre', masters.costCentre(asset.costCenterId)],
            ['Project', <ProjectName key="p" id={asset.projectId} />],
          ]}
        />
      </Section>
      <Section title="Current custody">
        <Facts
          label="Current custody"
          facts={[
            ['Custodian', <Custodian key="c" custody={currentCustody(asset)} />],
            ['Since', asset.custodySince ?? '—'],
            ['Acknowledgement', acknowledgementText(asset)],
          ]}
        />
      </Section>
      {can('assets.custody_assignment:view') && <CustodyHistory assetId={asset.id} />}
      <LifecycleEvents assetId={asset.id} />
      {/* Invoices, warranty cards, licences, photos (FRD-AST-013): attaching needs asset change. */}
      <DocumentsPanel recordType="assets.asset" recordId={asset.id} canAttach={canChange} />
      <MigrationTrace recordType="assets.asset" recordId={asset.id} />
      <RecordHistory recordType="assets.asset" recordId={asset.id} />
      {action === 'edit' && (
        <AssetDialog key={`${asset.id}:${String(asset.version)}`} asset={asset} onClose={close} />
      )}
      {action === 'status' && <StatusDialog asset={asset} onClose={close} />}
      {action === 'assign' && <AssignDialog asset={asset} onClose={close} />}
      {action === 'return' && <ReturnDialog asset={asset} onClose={close} />}
      {action === 'acknowledge' && <AcknowledgeDialog asset={asset} onClose={close} />}
    </Stack>
  );
}

function acknowledgementText(asset: AssetDto): string {
  if (asset.currentAssignmentId === null) return '—';
  if (asset.acknowledgementRequired !== true) return 'Not required';
  return asset.acknowledgedAt === null ? 'Pending' : `Acknowledged ${asset.acknowledgedAt}`;
}

/** The assets in the signed-in user's own custody; empty when no employee is linked. */
export function useMyAssets() {
  const session = useSession();
  return useQuery({
    queryKey: ['assets', 'my'],
    enabled: hasCapability(session.data, 'assets.asset:view'),
    retry: false,
    queryFn: async () => {
      try {
        return await getData('/api/v1/assets/my', z.array(AssetSchema));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

function SupplierName({ id }: { id: string | null }) {
  const session = useSession();
  const canSee = hasCapability(session.data, 'procurement.supplier:view');
  const supplier = useQuery({
    queryKey: ['procurement', 'supplier', id],
    enabled: canSee && id !== null,
    retry: false,
    queryFn: () => getData(`/api/v1/procurement/suppliers/${id ?? ''}`, SupplierSchema),
  });
  if (id === null) return <>—</>;
  if (!canSee) return <>Recorded (suppliers not visible to you)</>;
  if (supplier.isPending) return <>…</>;
  if (supplier.data === undefined) return <>Not visible</>;
  return (
    <RecordLink to={`/suppliers/${id}`}>
      {supplier.data.code} — {supplier.data.companyName}
    </RecordLink>
  );
}

/** Who acted, as far as the SPA may say without user administration rights. */
function useActor() {
  const session = useSession();
  return (userId: string | null) =>
    userId === null ? '—' : userId === session.data?.userId ? 'You' : `User #${userId}`;
}

/** The custody history, newest first; every row stays as it was recorded (FRD-AST-026). */
function CustodyHistory({ assetId }: { assetId: string }) {
  const actor = useActor();
  const history = useQuery({
    queryKey: ['assets', 'custody', assetId],
    queryFn: () =>
      getData(`${ASSETS}/${assetId}/custody-assignments`, z.array(CustodyAssignmentSchema)),
  });
  return (
    <Section title="Custody history">
      {history.isPending && <LoadingState label="Loading the custody history" />}
      {history.isError && <ErrorState message={history.error.message} />}
      {history.data?.length === 0 && <EmptyState title="Never assigned" />}
      {history.data !== undefined && history.data.length > 0 && (
        <Table size="small" aria-label="Custody history">
          <TableHead>
            <TableRow>
              <TableCell>Custodian</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Assigned by</TableCell>
              <TableCell>Acknowledgement</TableCell>
              <TableCell>Closed</TableCell>
              <TableCell>Condition at return</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Custodian custody={row} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.assignmentDate}</TableCell>
                <TableCell>{actor(row.assignedBy)}</TableCell>
                <TableCell>{historyAcknowledgement(row)}</TableCell>
                <TableCell>{closedText(row, actor)}</TableCell>
                <TableCell>{row.returnCondition ?? '—'}</TableCell>
                <TableCell>
                  <StatusChip status={row.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

function historyAcknowledgement(row: CustodyAssignmentDto): string {
  if (!row.acknowledgementRequired) return 'Not required';
  return row.acknowledgedAt === null ? 'Not acknowledged' : `Acknowledged ${row.acknowledgedAt}`;
}

function closedText(row: CustodyAssignmentDto, actor: (id: string | null) => string): string {
  if (row.closure === null) return '—';
  return row.closure === 'returned'
    ? `Returned ${row.returnDate ?? ''} (recorded by ${actor(row.returnedBy)})`
    : `Reassigned ${row.closedDate ?? ''}`;
}

/** The lifecycle status changes, newest first, each with its reason and actor (FRD-AST-050). */
function LifecycleEvents({ assetId }: { assetId: string }) {
  const actor = useActor();
  const events = useQuery({
    queryKey: ['assets', 'lifecycle', assetId],
    queryFn: () =>
      getData(`${ASSETS}/${assetId}/lifecycle-events`, z.array(AssetLifecycleEventSchema)),
  });
  return (
    <Section title="Status history">
      {events.isPending && <LoadingState label="Loading the status history" />}
      {events.isError && <ErrorState message={events.error.message} />}
      {events.data?.length === 0 && <EmptyState title="No status change yet" />}
      {events.data !== undefined && events.data.length > 0 && (
        <Table size="small" aria-label="Status history">
          <TableHead>
            <TableRow>
              <TableCell>Effective</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.data.map((event) => (
              <TableRow key={event.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{event.effectiveDate}</TableCell>
                <TableCell>{statusLabel(event.fromStatus)}</TableCell>
                <TableCell>{statusLabel(event.toStatus)}</TableCell>
                <TableCell>{event.reason}</TableCell>
                <TableCell>{actor(event.actorUserId)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}
